import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import ImageField from "./ImageField";

const inputCls = "w-full border border-navy/20 bg-white px-3.5 py-3 text-sm text-navy focus:border-gold";

const EMPTY = { title: "", body: "", category: "", author: "", cover_url: "", published: false };

export default function AdminArticles() {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/admin/articles").then((r) => setArticles(r.data || [])).catch(() => {});
    api.get("/admin/categories").then((r) => setCategories(r.data || [])).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      if (editing.id) {
        await api.put(`/admin/articles/${editing.id}`, editing);
        toast.success("Artigo atualizado.");
      } else {
        await api.post("/admin/articles", editing);
        toast.success("Artigo criado.");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não foi possível salvar o artigo.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/articles/${id}`);
      toast.success("Artigo excluído.");
      load();
    } catch (e) {
      toast.error("Não foi possível excluir.");
    }
  };

  const togglePublish = async (a) => {
    try {
      await api.put(`/admin/articles/${a.id}`, { ...a, published: !a.published });
      load();
    } catch (e) {
      toast.error("Não foi possível alterar a publicação.");
    }
  };

  return (
    <div data-testid="admin-articles">
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
        <h1 className="font-display text-3xl text-navy">Artigos</h1>
        <button
          data-testid="admin-article-new"
          onClick={() => setEditing({ ...EMPTY })}
          className="inline-flex items-center gap-2 bg-navy px-6 py-3 text-[11px] font-bold uppercase tracking-[0.2em] text-ivory hover:bg-navy-2"
        >
          <Plus size={14} /> Novo artigo
        </button>
      </div>

      <div className="mt-8 space-y-3">
        {articles.length === 0 && <p className="border border-dashed border-navy/20 bg-white px-6 py-10 text-sm text-inkgray">Nenhum artigo criado ainda.</p>}
        {articles.map((a) => (
          <div key={a.id} className="flex flex-col justify-between gap-4 border border-navy/10 bg-white p-5 md:flex-row md:items-center" data-testid={`admin-article-row-${a.slug}`}>
            <div className="flex items-center gap-4">
              {a.cover_url ? (
                <img src={a.cover_url} alt="" className="h-14 w-20 border border-navy/10 object-cover" />
              ) : (
                <img src="/media/capa-padrao.jpg" alt="" className="h-14 w-20 border border-navy/10 object-cover" />
              )}
              <div>
                <p className="font-semibold text-navy">{a.title}</p>
                <p className="mt-1 text-xs text-inkgray">
                  {a.category || "Sem categoria"} · {a.published ? "Publicado" : "Rascunho"}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button data-testid={`admin-article-publish-${a.slug}`} onClick={() => togglePublish(a)} className={`border px-4 py-2 text-[10px] font-bold uppercase tracking-[0.16em] ${a.published ? "border-gold text-gold-deep" : "border-navy/20 text-navy/60"}`}>
                {a.published ? "Despublicar" : "Publicar"}
              </button>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/conteudo-juridico/${a.slug}`} target="_blank" rel="noopener noreferrer" data-testid={`admin-article-view-${a.slug}`} className="border border-navy/20 p-2.5 text-navy hover:border-gold" title="Ver no site">
                <ExternalLink size={14} />
              </a>
              <button data-testid={`admin-article-edit-${a.slug}`} onClick={() => setEditing(a)} className="border border-navy/20 p-2.5 text-navy hover:border-gold" title="Editar">
                <Pencil size={14} />
              </button>
              <button data-testid={`admin-article-delete-${a.slug}`} onClick={() => remove(a.id)} className="border border-red-200 p-2.5 text-red-600 hover:bg-red-50" title="Excluir">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-navy/60 p-4 md:p-10" data-testid="admin-article-dialog">
          <div className="w-full max-w-2xl bg-white p-7 md:p-9">
            <h2 className="font-display text-2xl text-navy">{editing.id ? "Editar artigo" : "Novo artigo"}</h2>
            <div className="mt-6 space-y-5">
              <div>
                <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-navy">Título *</p>
                <input className={inputCls} data-testid="admin-article-title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-navy">Categoria</p>
                  <select className={inputCls} data-testid="admin-article-category" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}>
                    <option value="">Sem categoria</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-navy">Autor (opcional)</p>
                  <input className={inputCls} data-testid="admin-article-author" value={editing.author || ""} onChange={(e) => setEditing({ ...editing, author: e.target.value })} />
                </div>
              </div>
              <ImageField testid="admin-article-cover" label="Imagem de capa" value={editing.cover_url} onChange={(v) => setEditing({ ...editing, cover_url: v })} />
              <div>
                <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-navy">Texto — separe os parágrafos por linha em branco</p>
                <textarea rows={10} className={inputCls} data-testid="admin-article-body" value={editing.body || ""} onChange={(e) => setEditing({ ...editing, body: e.target.value })} />
              </div>
              <label className="flex items-center gap-3 text-sm text-navy">
                <input
                  type="checkbox"
                  data-testid="admin-article-published"
                  checked={!!editing.published}
                  onChange={(e) => setEditing({ ...editing, published: e.target.checked })}
                  className="h-4 w-4 accent-[#C9A15B]"
                />
                Publicado no site
              </label>
            </div>
            <div className="mt-8 flex gap-3">
              <button data-testid="admin-article-save" onClick={save} disabled={saving || !editing.title?.trim()} className="bg-navy px-6 py-3 text-[11px] font-bold uppercase tracking-[0.2em] text-ivory hover:bg-navy-2 disabled:opacity-60">
                {saving ? "Salvando…" : "Salvar"}
              </button>
              <button data-testid="admin-article-cancel" onClick={() => setEditing(null)} className="border border-navy/20 px-6 py-3 text-[11px] font-bold uppercase tracking-[0.2em] text-navy hover:border-gold">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
